import { useQuery } from '@apollo/client';
import { cloneDeep } from 'lodash';
import { createContext, useEffect, useMemo, useState } from 'react';
import { downloadXlsx } from '@/components/Downloads/helpers';
import {
  DEFAULT_TRACE_LIMIT,
  getMaxYFromBar,
  LEGEND_ONLY,
  TOP_SORT_DIRECTION,
} from '@/components/Graphs/constants';
import GraphControls from '@/components/Graphs/GraphControls';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { GOOGLE_ANALYTICS_TAG } from '../../constants';
import { GRAPH_ASSETS_QUERY_NEW } from '../operations';
import {
  barChartAggregateTypes,
  barGraphTypes,
  COUNTRY_AGGREGATE,
  featureFlaggedBarChartAggregateTypes,
  featureFlaggedBarGraphTypes,
  graphTitles,
  graphTypeChildren,
  NPV10,
} from '../utils';
import { useRange } from './hooks/useRange';

const BarGraphContextRight = createContext();
const visibleTraces = {};

export const BarGraphProviderRight = ({ children, portfolios }) => {
  const { portfolioAnalysisWellCounts } = useFeatureFlags();
  const [openControlsModal, setOpenControlsModal] = useState(false);

  const [aggregate, setAggregate] = useState(COUNTRY_AGGREGATE.name);
  const [graphType, setGraphType] = useState(NPV10);
  const [graphTypeOptions, setGraphTypeOptions] = useState([]);
  const [limit, setLimit] = useState(DEFAULT_TRACE_LIMIT);
  const [sortDirection, setSortDirection] = useState(TOP_SORT_DIRECTION);
  const [showPlotlyLegend, setShowPlotlyLegend] = useState(true);
  const [range, setRange, modalRange, setModalRange, resetRange] = useRange();

  const onRelayout = e => {
    const x = [e['xaxis.range[0]'], e['xaxis.range[1]']];
    const y = [e['yaxis.range[0]'], e['yaxis.range[1]']];
    setRange({ x, y });
  };

  const onGraphUpdate = () => {
    if (
      (range.x[0] !== modalRange.x[0] && range.x[1] !== modalRange.x[1]) ||
      (range.y[0] !== modalRange.y[0] && range.y[1] !== modalRange.y[1])
    ) {
      setModalRange(range);
    }
  };
  // TODO: permanently replace old queries with new ones
  const { data, loading } = useQuery(GRAPH_ASSETS_QUERY_NEW, {
    variables: {
      aggregates: [aggregate],
      chartType: 'bar',
      enableInfoOnClick: true,
      graphType: graphType.value,
      graphTypeOptions: graphTypeOptions.map(option => option.name),
      portfolios,
      limit: parseInt(limit, 10),
      sortDirection: sortDirection.value,
      visibleTraces,
    },
  });

  const trace = useMemo(() => {
    const analytics = data?.graphAssetsNew?.analytics;
    if (!analytics) return [];

    // Workaround for bug where the chart will not display if the marker color is set:
    return cloneDeep(analytics);
  }, [data]);

  const onLegendClick = data => {
    // get the index of the trace that was clicked
    const clickedIdx = data.curveNumber;
    const { visible } = data.fullData[clickedIdx];
    const clickedTrace = trace[clickedIdx];
    visibleTraces[clickedTrace.name] = visible === true ? LEGEND_ONLY : true;
    clickedTrace.visible = visibleTraces[clickedTrace.name];
    trace[clickedIdx] = clickedTrace;
    const maxY = getMaxYFromBar(trace);
    setRange({ x: range.x, y: [0, maxY] });
    setModalRange({ x: range.x, y: [0, maxY] });
  };

  const onLegendDoubleClick = data => {
    // get the index of the trace that was double clicked
    const clickedIdx = data.curveNumber;
    // if the trace that was double clicked is not visible then we want to show all
    // if the trace that was double clicked is visible then
    // a: hide all others if any others are visible
    // b: show all if all others are hidden
    const { visible } = data.fullData[clickedIdx];
    const hideAll =
      visible === true &&
      !!data.fullData.find((t, idx) => clickedIdx !== idx && t.visible === true);
    // toggle the traces
    trace.forEach((t, idx) => {
      const { name } = t;
      if (hideAll && clickedIdx !== idx) {
        visibleTraces[name] = LEGEND_ONLY;
      } else {
        visibleTraces[name] = true;
      }
    });
    if (hideAll) {
      setRange({ x: range.x, y: [0, Math.max(...trace[clickedIdx].y)] });
      setModalRange({ x: range.x, y: [0, Math.max(...trace[clickedIdx].y)] });
    } else {
      resetRange();
    }
  };

  useEffect(() => {
    void data;
    resetRange();
  }, [data, resetRange]);

  const downloadCallback = (errorHandler, successHandler) => {
    const path = 'portfolio-analysis/export-asset-bar-graph-data/';
    const body = {
      aggregate,
      graphType: graphType.value,
      graphTypeOptions: graphTypeChildren[graphType.value]?.map(option => option.name) || [],
      portfolios,
      limit,
      sortDirection: sortDirection.value,
      visibleTraces,
    };
    const downloadName = `${portfolios[0].name}_Asset_Data_by_${aggregate}.xlsx`;

    downloadXlsx(path, body, downloadName, errorHandler, successHandler);
  };

  return (
    <BarGraphContextRight.Provider
      value={{
        aggregate,
        downloadCallback,
        graphType,
        onLegendClick,
        loading,
        modalRange,
        onGraphUpdate,
        onLegendDoubleClick,
        onRelayout,
        range,
        setModalRange,
        setOpenControlsModal,
        setShowPlotlyLegend,
        showPlotlyLegend,
        showPlotlyLegendToggle: !!graphTypeChildren[graphType.value] && graphTypeOptions.length > 0,
        sortDirection,
        trace,
        maxY: data?.graphAssetsNew?.maxY || [],
        googleAnalyticsTag: GOOGLE_ANALYTICS_TAG,
        graphTitles,
      }}
    >
      {children}
      <GraphControls
        aggregate={aggregate}
        aggregateTypes={
          portfolioAnalysisWellCounts
            ? featureFlaggedBarChartAggregateTypes
            : barChartAggregateTypes
        }
        googleAnalyticsTag={GOOGLE_ANALYTICS_TAG}
        graphType={graphType}
        graphTypeChildren={graphTypeChildren}
        graphTypeOptions={graphTypeOptions}
        includeOptions
        limit={limit}
        open={openControlsModal}
        setAggregate={setAggregate}
        setGraphType={setGraphType}
        setGraphTypeOptions={setGraphTypeOptions}
        setLimit={setLimit}
        setOpen={setOpenControlsModal}
        setSortDirection={setSortDirection}
        sortDirection={sortDirection}
        types={portfolioAnalysisWellCounts ? featureFlaggedBarGraphTypes : barGraphTypes}
      />
    </BarGraphContextRight.Provider>
  );
};

export const BarGraphConsumerRight = BarGraphContextRight.Consumer;

export default BarGraphContextRight;
